import React, { useMemo } from "react";
import { usePreviewData } from "../../../data";
import styled from "styled-components";
import MainCard from "@appsinti/ui-core/MainCard";
import PreviewSection from "./PreviewSection";
import {
  HeightRestriction,
  WidthRestriction,
} from "../../../../interfaces/Restrictions";
import DaySection from "./DaySection";
import { useDateProvider } from "@appsinti/date-provider";
import { useCurrencySelector } from "@appsinti/currency-provider";
import StatsPreview from "../../../../interfaces/StatsPreview";
import StatsDay from "../../../../interfaces/StatsDay";

const CustomMainCard = styled(MainCard)`

  > header {
    padding: 3px 10px 3px 10px;

    @media screen and (max-height: 750px) {
      padding: 2px 8px 2px 8px;
    }

    @media screen and (max-height: 710px) {
      padding: 1px 6px 1px 6px;
    }
  }

  > .body {
    gap: 5px;
    padding: 5px;

    @media screen and (max-height: 750px) {
      padding: 3px;
      gap: 4px;
    }
  }
`;

const Preview: React.FC<{
  heightRestriction: HeightRestriction;
  widthRestriction: WidthRestriction;
  preview: StatsPreview | null;
  loading: boolean;
  lastUpdatedTime?: Date;
}> = ({ heightRestriction, widthRestriction, preview, loading, lastUpdatedTime }) => {
  const { currency } = useCurrencySelector();

  const subtitle = useMemo(() => {
    if (
      (currency === undefined || currency === null) &&
      preview?.salesData.currency
    ) {
      return `(${preview.salesData.currency})`;
    }
    if (currency === undefined) {
      return undefined;
    }
    if (
      currency &&
      preview?.salesData.conversions &&
      !!preview?.salesData.currency &&
      preview?.salesData.currency !== currency &&
      preview?.salesData.conversions[currency] &&
      preview?.salesData.conversions[currency].rate
    ) {
      return `(1 ${currency} = ${(1 / preview.salesData.conversions[currency].rate).toFixed(3)} ${preview.salesData.currency})`;
    }
    if (preview?.salesData.currency && (currency === null || currency === preview.salesData.currency)) {
      return `(${preview.salesData.currency})`;
    }
    return undefined;
  }, [currency, preview]);

  return (
    <CustomMainCard title={"Sales"} subtitle={subtitle} lastUpdatedTime={lastUpdatedTime} halfWidth={widthRestriction === "half-width"}>
      <PreviewSection
        preview={preview}
        loading={loading}
        heightRestriction={heightRestriction}
        widthRestriction={widthRestriction}
      />
    </CustomMainCard>
  );
};

const Day: React.FC<{
  heightRestriction: HeightRestriction;
  widthRestriction: WidthRestriction;
  day: StatsDay | null;
  loading: boolean;
  lastUpdatedTime?: Date;
}> = ({ heightRestriction, widthRestriction, day, loading, lastUpdatedTime }) => {
  const { currency } = useCurrencySelector();

  const subtitle = useMemo(() => {
    if (
      (currency === undefined || currency === null) &&
      day?.salesData.currency
    ) {
      return `(${day.salesData.currency})`;
    }
    if (currency === undefined) {
      return undefined;
    }
    if (
      currency &&
      day?.salesData.conversions &&
      !!day?.salesData.currency &&
      day?.salesData.currency !== currency &&
      day?.salesData.conversions[currency] &&
      day?.salesData.conversions[currency].rate
    ) {
      return `(1 ${currency} = ${(1 / day.salesData.conversions[currency].rate).toFixed(3)} ${day.salesData.currency})`;
    }
    if (day?.salesData.currency && (currency === null || currency === day.salesData.currency)) {
      return `(${day.salesData.currency})`;
    }
    return undefined;
  }, [currency, day]);

  return (
    <CustomMainCard title={"Sales"} subtitle={subtitle} lastUpdatedTime={lastUpdatedTime} halfWidth={widthRestriction === "half-width"}>
      <DaySection
        day={day}
        loading={loading}
        heightRestriction={heightRestriction}
        widthRestriction={widthRestriction}
      />
    </CustomMainCard>
  );
};

const Group: React.FC<{
  heightRestriction: HeightRestriction;
  widthRestriction: WidthRestriction;
  groupId: number;
}> = ({ heightRestriction, widthRestriction, groupId }) => {
  const { preview, day, loadingPreview, loadingDay } = usePreviewData();
  const { currentDate } = useDateProvider();

  const lastUpdatedTime = useMemo(() => {
    if (preview?.[groupId] && preview[groupId].lastUpdate) {
      return new Date(preview[groupId].lastUpdate);
    }
  }, [preview, groupId]);

  return (
    <>
      {currentDate ? (
        <Day
          day={day?.[groupId] || null}
          loading={loadingDay(groupId)}
          heightRestriction={heightRestriction}
          widthRestriction={widthRestriction}
          lastUpdatedTime={lastUpdatedTime}
        />
      ) : (
        <Preview
          preview={preview?.[groupId] || null}
          loading={loadingPreview(groupId)}
          heightRestriction={heightRestriction}
          widthRestriction={widthRestriction}
          lastUpdatedTime={lastUpdatedTime}
        />
      )}
    </>
  );
};

export default Group;
